<template>
  <div class="payroll-page">
    <!-- Header + main action -->
    <div class="payroll-header d-flex flex-wrap justify-content-between align-items-center mb-2">
      <div class="mb-1 mb-md-0">
        <h2 class="mb-25 d-flex align-items-center">
          <feather-icon
            icon="CalendarIcon"
            :size="'22'"
            class="mr-50 text-primary"
          />
          <span>Payroll Calendar</span>
        </h2>
        <small class="text-muted">
          Visual overview of all payroll periods, grouped by year and month.
        </small>
      </div>
      <div class="d-flex align-items-center">
        <b-spinner
          v-if="localDataLoad"
          small
          class="mr-50"
        />
        <b-button
          variant="gradient-primary"
          class="d-flex align-items-center payroll-generate-btn"
          @click="handleGeneratePayroll"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :size="'16'"
            class="mr-50"
          />
          <span>Generate / Re-generate Payroll</span>
        </b-button>
      </div>
    </div>

    <div class="payroll-layout">
      <!-- Left: vertical year list -->
      <div class="payroll-years-nav">
        <div class="years-title text-uppercase text-muted small mb-1">
          Years
        </div>
        <div class="years-list">
          <button
            v-for="(year, index) in optionsPayroll"
            :key="year.year"
            type="button"
            class="year-pill"
            :class="{ 'year-pill--active': activeYearIndex === index }"
            @click="activeYearIndex = index"
          >
            <span class="year-pill__year">{{ year.year }}</span>
            <span class="year-pill__months">{{ year.months.length }} mo</span>
          </button>
        </div>
      </div>

      <!-- Right: active year calendar -->
      <div class="payroll-year-view">
        <transition
          name="fade-slide"
          mode="out-in"
        >
          <div
            :key="activeYear.year"
            class="payroll-year-card mb-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div>
                <h4 class="mb-25">
                  {{ activeYear.year }}
                </h4>
                <small class="text-muted">
                  {{ activeYear.months.length }} payroll {{ activeYear.months.length === 1 ? 'month' : 'months' }}
                </small>
              </div>
              <b-badge variant="light-primary">
                Year {{ activeYearIndex + 1 }}
              </b-badge>
            </div>

            <!-- Month grid like a calendar -->
            <div class="payroll-month-grid">
              <div
                v-for="month in activeYear.months"
                :key="month.id"
                class="payroll-month-cell"
              >
                <div
                  class="payroll-month-card"
                  @click="showPayroll(month.id)"
                >
                  <div class="payroll-month-header d-flex justify-content-between align-items-center mb-25">
                    <div class="d-flex flex-column">
                      <span class="month-name text-truncate">
                        {{ month.name }}
                      </span>
                      <small class="text-muted">
                        Period
                      </small>
                    </div>
                    <feather-icon
                      icon="ArrowRightCircleIcon"
                      :size="'18'"
                      class="text-primary"
                    />
                  </div>
                  <div class="payroll-month-dates text-muted small">
                    <div>
                      <feather-icon
                        icon="ArrowDownLeftIcon"
                        :size="'12'"
                        class="mr-25 text-success"
                      />
                      From: {{ month.from }}
                    </div>
                    <div>
                      <feather-icon
                        icon="ArrowUpRightIcon"
                        :size="'12'"
                        class="mr-25 text-danger"
                      />
                      To: {{ month.to }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  components: {
    FeatherIcon,
  },
  data() {
    return {
      localDataLoad: false,
      activeYearIndex: 0,
    }
  },

  computed: {
    activeYear() {
      if (!this.optionsPayroll || !this.optionsPayroll.length) return { year: '-', months: [] }
      const index = Math.min(this.activeYearIndex, this.optionsPayroll.length - 1)
      return this.optionsPayroll[index]
    },
    optionsPayroll() {
      return this.$store.getters['generalData/payroll/years']
    },
  },

  async mounted() {
    if (!this.$store.getters['generalData/payroll/payrollLoaded']) {
      this.localDataLoad = true
      await this.$store.dispatch('generalData/payroll/fetchPayroll')
      this.localDataLoad = false
    }

    // Set current year as default active if available
    if (this.optionsPayroll && this.optionsPayroll.length) {
      const currentYear = new Date().getFullYear()
      const idx = this.optionsPayroll.findIndex(y => Number(y.year) === currentYear)
      this.activeYearIndex = idx !== -1 ? idx : 0
    }
  },
  methods: {
    async handleGeneratePayroll() {
      const result = await this.$swal.fire({
        title: 'Generate payroll?',
        text: 'This will generate or re-generate payroll.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
      })

      if (!result.isConfirmed) return

      try {
        this.localDataLoad = true
        await axios.post('payroll/generate', {})
        await this.$store.dispatch('generalData/payroll/fetchPayroll')
        this.$swal.fire('Done', 'Payroll generated successfully.', 'success')
      } catch (error) {
        this.$swal.fire('Error', 'Failed to generate payroll. Please try again.', 'error')
      } finally {
        this.localDataLoad = false
      }
    },
    showPayroll(id) {
      this.$router.push({ name: 'payroll-details', params: { id } })
    },
  },
}
</script>

<style scoped>
.payroll-page {
  padding: 0.5rem 0.75rem;
  background: radial-gradient(circle at top left, #f5f4ff 0, #ffffff 45%, #f8f9fb 100%);
}

.payroll-month-card {
  border-radius: 12px;
  border: 1px solid #edf0f7;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s ease;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(25, 42, 70, 0.04);
}

.payroll-month-card:hover {
  box-shadow: 0 10px 24px rgba(25, 42, 70, 0.18);
  transform: translateY(-4px);
  border-color: #7367f0;
}

.payroll-month-card .text-muted {
  font-size: 0.75rem;
}

.payroll-header h2 {
  font-weight: 600;
}

.payroll-generate-btn {
  border-radius: 999px;
  padding: 0.45rem 1.1rem;
}

.payroll-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  grid-column-gap: 1.25rem;
  align-items: stretch;
}

.payroll-years-nav {
  background: #ffffff;
  border-radius: 16px;
  padding: 0.75rem 0.75rem;
  box-shadow: 0 8px 20px rgba(25, 42, 70, 0.05);
  border: 1px solid #edf0f7;
  height: 100%;
}

.years-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.year-pill {
  border-radius: 999px;
  border: none;
  padding: 0.35rem 0.9rem;
  background: #f3f4fb;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8rem;
}

.year-pill:hover {
  background: #e4e6fb;
}

.year-pill--active {
  background: linear-gradient(135deg, #7367f0, #9e95ff);
  color: #ffffff;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.45);
}

.year-pill__year {
  font-weight: 600;
}

.year-pill__months {
  opacity: 0.85;
}

.payroll-year-view {
  position: relative;
  height: 100%;
}

.payroll-year-card {
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(25, 42, 70, 0.12);
  border: none;
  padding: 1.25rem 1.4rem;
  background: linear-gradient(135deg, #ffffff, #f8f7ff);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.payroll-month-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-gap: 0.9rem;
  margin-top: 0.75rem;
  flex: 1;
}

.payroll-month-cell {
  min-width: 0;
}

.month-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.payroll-month-dates > div {
  display: flex;
  align-items: center;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter {
  opacity: 0;
  transform: translateY(6px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 991.98px) {
  .payroll-layout {
    grid-template-columns: 1fr;
    grid-row-gap: 0.75rem;
  }

  .payroll-years-nav {
    max-height: none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .years-list {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .year-pill {
    flex: 0 0 auto;
  }
}
</style>
